/**
 * hero-carousel.js
 * 宮崎キャビア1983 Hero Peek Carousel
 * scroll-snap ベース、自動再生・ドット・矢印ナビ付き
 * 無限ループ: クローンスライド方式
 */
(function () {
  'use strict';

  class HeroCarousel {
    constructor(el) {
      this.el = el;
      this.track = el.querySelector('.hero-carousel__track');
      this.slides = [...el.querySelectorAll('.hero-carousel__slide')];
      this.dots = [...el.querySelectorAll('.hero-carousel__dot')];
      this.prevBtn = el.querySelector('.hero-carousel__arrow--prev');
      this.nextBtn = el.querySelector('.hero-carousel__arrow--next');

      this.currentIndex = 0;
      this.realCount = this.slides.length;
      this.autoplayEnabled = el.dataset.autoplay === 'true';
      this.interval = (parseInt(el.dataset.interval, 10) || 5) * 1000;
      this.autoplayTimer = null;
      this.isHovered = false;
      this.isTouching = false;
      this.scrollEndTimer = null;

      if (this.slides.length < 2) return;

      this._createClones();
      // クローン含む全スライドを取得
      this.allSlides = [...this.track.querySelectorAll('.hero-carousel__slide')];

      this._bindEvents();
      // 初期位置: 実スライド1へ（アニメーションなし）
      this.track.style.scrollBehavior = 'auto';
      requestAnimationFrame(() => {
        this._scrollToSlide(1);
        requestAnimationFrame(() => {
          this.track.style.scrollBehavior = 'smooth';
        });
      });
      this._updateNav();
      if (this.autoplayEnabled) this._startAutoplay();
    }

    _createClones() {
      const firstClone = this.slides[0].cloneNode(true);
      const lastClone = this.slides[this.slides.length - 1].cloneNode(true);

      [firstClone, lastClone].forEach(clone => {
        clone.setAttribute('data-clone', 'true');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('role');
        clone.removeAttribute('aria-roledescription');
        clone.removeAttribute('aria-label');
        if (clone.id) clone.removeAttribute('id');
      });

      // 末尾にfirstClone、先頭にlastCloneを挿入
      // 結果: [lastClone] [slide1] [slide2] [slide3] [firstClone]
      this.track.appendChild(firstClone);
      this.track.insertBefore(lastClone, this.track.firstChild);
    }

    _scrollToSlide(allIndex) {
      const slide = this.allSlides[allIndex];
      if (!slide) return;
      const targetLeft = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
      this.track.scrollTo({ left: targetLeft });
    }

    _silentScrollTo(allIndex) {
      this.track.style.scrollBehavior = 'auto';
      this._scrollToSlide(allIndex);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.track.style.scrollBehavior = 'smooth';
        });
      });
    }

    _bindEvents() {
      // スクロール終了を検知してアクティブスライドを更新
      this.track.addEventListener('scroll', () => {
        clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = setTimeout(() => this._onScrollEnd(), 100);
      }, { passive: true });

      // ドットクリック
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => this._goTo(i));
      });

      // 前へ / 次へ矢印
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this._goPrev());
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this._goNext());
      }

      // hover で自動再生一時停止 (PC)
      this.el.addEventListener('mouseenter', () => {
        this.isHovered = true;
        this._stopAutoplay();
      });
      this.el.addEventListener('mouseleave', () => {
        this.isHovered = false;
        if (this.autoplayEnabled) this._startAutoplay();
      });

      // touch で自動再生一時停止 (モバイル)
      this.track.addEventListener('touchstart', () => {
        this.isTouching = true;
        this._stopAutoplay();
      }, { passive: true });
      this.track.addEventListener('touchend', () => {
        this.isTouching = false;
        if (this.autoplayEnabled) this._startAutoplay();
      }, { passive: true });

      // prefers-reduced-motion 対応
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (mq.matches) {
        this.autoplayEnabled = false;
        this._stopAutoplay();
      }
      mq.addEventListener('change', (e) => {
        if (e.matches) {
          this.autoplayEnabled = false;
          this._stopAutoplay();
        }
      });
    }

    _onScrollEnd() {
      // トラック中央に最も近いスライドを検出
      const trackRect = this.track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;
      let closestIndex = 0;
      let closestDist = Infinity;

      this.allSlides.forEach((slide, i) => {
        const rect = slide.getBoundingClientRect();
        const slideCenter = rect.left + rect.width / 2;
        const dist = Math.abs(slideCenter - trackCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closestIndex = i;
        }
      });

      const lastCloneIndex = 0;                          // 先頭のclone（元は最後のスライド）
      const firstCloneIndex = this.allSlides.length - 1; // 末尾のclone（元は最初のスライド）

      if (closestIndex === firstCloneIndex) {
        // 末尾クローン（clone1）→ 実slide1へジャンプ
        this._silentScrollTo(1);
        this.currentIndex = 0;
        this._updateNav();
      } else if (closestIndex === lastCloneIndex) {
        // 先頭クローン（clone3）→ 実slide3へジャンプ
        this._silentScrollTo(this.realCount);
        this.currentIndex = this.realCount - 1;
        this._updateNav();
      } else {
        // 通常スライド: allIndex → realIndex（-1 for prepended clone）
        const realIndex = closestIndex - 1;
        if (realIndex !== this.currentIndex) {
          this.currentIndex = realIndex;
          this._updateNav();
        }
      }
    }

    _goTo(index) {
      // realIndex → allIndex（+1 for prepended clone）
      const allIndex = index + 1;
      const slide = this.allSlides[allIndex];
      if (!slide) return;
      const targetLeft = slide.offsetLeft - (this.track.clientWidth - slide.offsetWidth) / 2;
      this.track.scrollTo({ left: targetLeft, behavior: 'smooth' });
      this.currentIndex = index;
      this._updateNav();
    }

    _goPrev() {
      if (this.currentIndex > 0) {
        this._goTo(this.currentIndex - 1);
      } else {
        // 実slide1にいる → 先頭クローン（lastClone）へスクロール → _onScrollEndでリセット
        this._scrollToSlide(0);
        this.currentIndex = this.realCount - 1;
        this._updateNav();
      }
    }

    _goNext() {
      if (this.currentIndex < this.realCount - 1) {
        this._goTo(this.currentIndex + 1);
      } else {
        // 実最後にいる → 末尾クローン（firstClone）へスクロール → _onScrollEndでリセット
        this._scrollToSlide(this.allSlides.length - 1);
        this.currentIndex = 0;
        this._updateNav();
      }
    }

    _updateNav() {
      // ドット更新（実スライド数分のみ）
      this.dots.forEach((dot, i) => {
        const isActive = i === this.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', String(isActive));
      });

      // 矢印は常に有効（disabled なし）
      if (this.prevBtn) this.prevBtn.disabled = false;
      if (this.nextBtn) this.nextBtn.disabled = false;
    }

    _startAutoplay() {
      this._stopAutoplay();
      if (!this.autoplayEnabled || this.isHovered || this.isTouching) return;
      this.autoplayTimer = setInterval(() => {
        if (this.currentIndex < this.realCount - 1) {
          this._goTo(this.currentIndex + 1);
        } else {
          // 最後 → 末尾クローンへスクロール → _onScrollEndで実slide1にリセット
          this._scrollToSlide(this.allSlides.length - 1);
          this.currentIndex = 0;
          this._updateNav();
        }
      }, this.interval);
    }

    _stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.hero-carousel').forEach((el) => new HeroCarousel(el));
  });
})();
